import React, { useState, useEffect } from "react";
import {
  Shield,
  Camera,
  Mic,
  Monitor,
  AlertTriangle,
  CheckCircle,
  X,
  Eye,
  Lock,
} from "lucide-react";

const ProctoringSetupModal = ({
  isOpen,
  onClose,
  onStartProctoring,
  requireCamera = true,
  requireMicrophone = true,
}) => {
  const [cameraPermission, setCameraPermission] = useState("pending");
  const [microphonePermission, setMicrophonePermission] = useState("pending");
  const [fullscreenSupported, setFullscreenSupported] = useState(true);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    // Check fullscreen support
    setFullscreenSupported(!!document.documentElement.requestFullscreen);

    // Check existing permissions
    checkPermissions();
  }, [isOpen]);

  const checkPermissions = async () => {
    try {
      // Check camera permission
      if (requireCamera) {
        const cameraPermissionStatus = await navigator.permissions.query({
          name: "camera",
        });
        setCameraPermission(
          cameraPermissionStatus.state === "granted" ? "granted" : "pending"
        );
      }

      // Check microphone permission
      if (requireMicrophone) {
        const microphonePermissionStatus = await navigator.permissions.query({
          name: "microphone",
        });
        setMicrophonePermission(
          microphonePermissionStatus.state === "granted"
            ? "granted"
            : "pending"
        );
      }
    } catch (error) {
      console.warn("Permission API not supported:", error);
    }
  };

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraPermission("granted");
      // Stop the stream as we just needed permission
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      setCameraPermission("denied");
      console.error("Camera permission denied:", error);
    }
  };

  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicrophonePermission("granted");
      // Stop the stream as we just needed permission
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      setMicrophonePermission("denied");
      console.error("Microphone permission denied:", error);
    }
  };

  const canProceed = () => {
    const cameraReady = !requireCamera || cameraPermission === "granted";
    const microphoneReady =
      !requireMicrophone || microphonePermission === "granted";
    return cameraReady && microphoneReady && hasAcceptedTerms;
  };

  const handleStartProctoring = () => {
    if (canProceed()) {
      onStartProctoring();
      // Don't call onClose() here - let the parent component handle it
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Shield className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Proctoring Setup
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Introduction */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">
                  Proctored Assessment
                </h3>
                <p className="text-sm text-blue-800 mt-1">
                  This assessment is proctored to ensure fairness and integrity.
                  We'll monitor your activity during the test to prevent
                  cheating.
                </p>
              </div>
            </div>
          </div>

          {/* Proctoring Features */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">
              What will be monitored:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Eye className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">Tab Switching</p>
                  <p className="text-sm text-gray-600">
                    Detect when you leave this page
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Lock className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">Copy/Paste</p>
                  <p className="text-sm text-gray-600">
                    Prevent copying content
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Monitor className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">Fullscreen</p>
                  <p className="text-sm text-gray-600">
                    Enforce fullscreen mode
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Shield className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">Right-click</p>
                  <p className="text-sm text-gray-600">Disable context menu</p>
                </div>
              </div>
            </div>
          </div>

          {/* Permission Requests */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">
              Required Permissions:
            </h3>

            {requireCamera && (
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Camera className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Camera Access</p>
                    <p className="text-sm text-gray-600">
                      Monitor your presence during the test
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {cameraPermission === "granted" ? (
                    <div className="flex items-center space-x-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Granted</span>
                    </div>
                  ) : cameraPermission === "denied" ? (
                    <div className="text-red-600 text-sm">Denied</div>
                  ) : (
                    <button
                      onClick={requestCameraPermission}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Allow
                    </button>
                  )}
                </div>
              </div>
            )}

            {requireMicrophone && (
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Mic className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Microphone Access</p>
                    <p className="text-sm text-gray-600">
                      Monitor audio during the test
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {microphonePermission === "granted" ? (
                    <div className="flex items-center space-x-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Granted</span>
                    </div>
                  ) : microphonePermission === "denied" ? (
                    <div className="text-red-600 text-sm">Denied</div>
                  ) : (
                    <button
                      onClick={requestMicrophonePermission}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                    >
                      Allow
                    </button>
                  )}
                </div>
              </div>
            )}

            {!fullscreenSupported && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  <p className="text-sm text-yellow-800">
                    Fullscreen mode is not supported in your browser. Some
                    proctoring features may be limited.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className="space-y-3">
            <div className="border border-gray-200 rounded-lg p-4">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={hasAcceptedTerms}
                  onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <div className="text-sm text-gray-700">
                  <p className="font-medium mb-1">
                    I understand and agree to the proctoring terms:
                  </p>
                  <ul className="space-y-1 text-gray-600">
                    <li>
                      • I will not switch tabs, open new windows, or use other
                      applications
                    </li>
                    <li>
                      • I will not copy, paste, or use external resources unless
                      explicitly allowed
                    </li>
                    <li>
                      • I will remain in fullscreen mode throughout the
                      assessment
                    </li>
                    <li>
                      • I understand that violations may result in test
                      termination
                    </li>
                    <li>
                      • I consent to camera and microphone monitoring if
                      required
                    </li>
                  </ul>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleStartProctoring}
            disabled={!canProceed()}
            className={`px-6 py-2 rounded-lg font-medium ${
              canProceed()
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Start Proctored Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProctoringSetupModal;

